/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

/**
 * This file serves as a centralized mock backend service.
 * It consolidates simulated API calls for various features like booking,
 * cloud storage, and social media integration.
 * 
 * NOTE: Real backend implementation code is provided in comments within each function.
 * To switch to the real backend, uncomment the code blocks and ensure APP_CONFIG.API_BASE_URL is set.
 */

import { BookingDetails, CloudData, InstagramReel, YouTubeVideo, Tweet, UserProfile, VoucherDetails, ColorAnalysisResult, ContactDetails } from '../types';
import { instagramReels, youTubeVideos, tweets } from '../data/testimonialsData';
import { API_KEYS, APP_CONFIG } from '../config';
import { Logger } from './logger';
import { sendEmail, buildBookingConfirmationEmail, buildContactFormEmail, buildVoucherPurchaseEmail } from './emailService';
import { googleCalendarInsert, getMeetLinkFromEvent } from './googleWorkspaceService';
import { saveDocumentToFirestore, getDocumentFromFirestore } from './gcpStorageService';

const CLOUD_STORAGE_KEY_PREFIX = 'vto_cloud_storage_';
const simulateNetworkDelay = (delay = 1000) => new Promise(resolve => setTimeout(resolve, delay));

// Helper to validate configuration keys
const validateConfig = (value: string | undefined, name: string): boolean => {
    if (!value || value.includes('PLACEHOLDER')) {
        Logger.warn(`Missing configuration for ${name}. Using mock/simulation mode.`);
        return false;
    }
    return true;
};

// --- Configuration Service ---
export const getAppConfiguration = async (): Promise<typeof APP_CONFIG> => {
    Logger.info('Fetching application configuration...');
    await simulateNetworkDelay(300);
    return APP_CONFIG;
};

// --- Authentication & User Management ---
export const authenticateUser = async (userId: string, password?: string): Promise<{ success: boolean; message?: string; token?: string }> => {
    Logger.info('Authenticating user', { userId });
    await simulateNetworkDelay(500);

    if (userId.toLowerCase().includes('admin')) {
        if (password === APP_CONFIG.ADMIN_PASSWORD) { // Fallback
            Logger.info('Admin authentication successful');
            return { success: true, token: 'admin-mock-token' };
        }
        Logger.warn('Admin authentication failed: Incorrect password');
        return { success: false, message: 'Invalid admin password' };
    }

    return { success: true, token: 'user-mock-token' };
};

// --- User Registration ---
export const registerUser = async (email: string, password: string): Promise<{ success: boolean; userId?: string; message?: string }> => {
    Logger.info(`Registering new user: ${email}`);
    await simulateNetworkDelay(800);

    const { createRegistrationProfile } = await import('./registrationService');
    const { buildActivationEmail, buildAdminNotificationEmail, sendEmail } = await import('./emailService');

    try {
        const userId = email.split('@')[0] + '_' + Date.now();
        const registrationProfile = createRegistrationProfile(email);

        try {
            await saveDocumentToFirestore('users', userId, registrationProfile);
        } catch (firestoreError) {
            // Fallback to localStorage
            const { saveUserProfile } = await import('./userService');
            saveUserProfile(userId, registrationProfile as any);
        }

        const activationLink = `${APP_CONFIG.DOMAIN}/activate?token=${registrationProfile.activationToken}`;
        const activationEmail = buildActivationEmail(email, activationLink);

        try {
            await sendEmail(email, activationEmail.subject, activationEmail.html, activationEmail.text);
        } catch (emailError) {
            Logger.warn('Failed to send activation email', emailError);
        }

        const adminEmail = buildAdminNotificationEmail(email, registrationProfile.registrationDate!);
        try {
            await sendEmail(APP_CONFIG.EMAIL_TO, adminEmail.subject, adminEmail.html, adminEmail.text);
        } catch (emailError) { }

        return {
            success: true,
            userId,
            message: 'Registration successful. Please check your email to activate your account.'
        };
    } catch (error) {
        return { success: false, message: 'Registration failed. Please try again later.' };
    }
};

// --- Booking Service ---
export const submitBooking = async (details: BookingDetails): Promise<{ confirmation: string; date: string }> => {
    Logger.info('Simulating booking submission with details:', details);
    await simulateNetworkDelay(1500);

    try {
        const eventId = await googleCalendarInsert(details);
        const meetLink = await getMeetLinkFromEvent(eventId);

        if (details.email === 'fail@example.com') throw new Error('Simulated booking failure');

        const emailContent = buildBookingConfirmationEmail(details, meetLink, eventId);
        await sendEmail(APP_CONFIG.EMAIL_TO, emailContent.subject, emailContent.html, emailContent.text);

        const bookingDate = new Date();
        bookingDate.setDate(bookingDate.getDate() + 7);

        return { confirmation: eventId, date: bookingDate.toLocaleDateString() };
    } catch (error) {
        throw new Error('Failed to coordinate booking with Google Services.');
    }
};

// --- Contact Service ---
export const submitContact = async (details: ContactDetails): Promise<{ success: boolean; messageId: string }> => {
    Logger.info('Processing contact form submission:', details);
    await simulateNetworkDelay(1000);

    if (!details.name || !details.email || !details.message) throw new Error('Please fill in all required fields.');
    if (!details.email.includes('@')) throw new Error('Please provide a valid email address.');

    try {
        const emailContent = buildContactFormEmail(details);
        await sendEmail(APP_CONFIG.EMAIL_TO, emailContent.subject, emailContent.html, emailContent.text);

        const messageId = `MSG-${Date.now().toString(36).toUpperCase()}`;
        return { success: true, messageId };
    } catch (error) {
        throw new Error('Failed to send your message.');
    }
};

// --- Voucher Service ---
export const submitVoucherPurchase = async (details: VoucherDetails): Promise<{ success: boolean; voucherCode: string }> => {
    Logger.info('Processing voucher purchase:', details);
    await simulateNetworkDelay(1200);

    try {
        const voucherCode = `GIFT-${Math.random().toString(36).substring(2, 11).toUpperCase()}`;
        const emailContent = buildVoucherPurchaseEmail(details, voucherCode);
        await sendEmail(
            APP_CONFIG.EMAIL_TO,
            emailContent.subject,
            emailContent.html,
            emailContent.text
        );
        return { success: true, voucherCode };
    } catch (error) {
        throw new Error('Failed to process voucher purchase.');
    }
};

// --- Membership Service ---
export const processMembershipPayment = async (amount: string, tierId: string, paymentDetails: any): Promise<{ success: boolean; transactionId: string }> => {
    Logger.info(`Processing payment of ${amount} for ${tierId}`);
    await simulateNetworkDelay(1000);
    return { success: true, transactionId: `TX-OFFLINE-${Date.now().toString().slice(-6)}` };
};

// --- Storage Service ---
export const saveUserDataToCloud = async (userId: string, data: CloudData): Promise<void> => {
    Logger.info('Saving user data to cloud', { userId });

    try {
        await saveDocumentToFirestore('users', userId, data);
        return;
    } catch (e) {
        Logger.error('Failed to save to Firestore, falling back to local simulation', e);
    }

    return new Promise((resolve, reject) => {
        setTimeout(() => {
            try {
                localStorage.setItem(`${CLOUD_STORAGE_KEY_PREFIX}${userId}`, JSON.stringify(data));
                resolve();
            } catch (error) {
                reject(new Error('Failed to save data to the simulated cloud.'));
            }
        }, 1500);
    });
};

export const loadUserDataFromCloud = async (userId: string): Promise<CloudData | null> => {
    Logger.info('Loading user data from cloud', { userId });

    try {
        const data = await getDocumentFromFirestore('users', userId);
        if (data) {
            const isDefaultAdmin = userId.toLowerCase().includes('admin');
            const defaultProfile: UserProfile = {
                measurements: {},
                membershipLevel: isDefaultAdmin ? 'advanced' : 'basic',
                saveImageQuality: 'medium'
            };
            return {
                outfits: data.outfits || [],
                lookbooks: data.lookbooks || [],
                profile: { ...defaultProfile, ...(data.profile || {}) },
                savedContent: data.savedContent || [],
                favorites: data.favorites || [],
            };
        }
    } catch (e) {
        Logger.error('Failed to load from Firestore, falling back to local simulation', e);
    }

    return new Promise((resolve, reject) => {
        setTimeout(() => {
            try {
                const rawData = localStorage.getItem(`${CLOUD_STORAGE_KEY_PREFIX}${userId}`);
                if (rawData) {
                    const data = JSON.parse(rawData);
                    const isDefaultAdmin = userId.toLowerCase().includes('admin');
                    const defaultProfile: UserProfile = {
                        measurements: {},
                        membershipLevel: isDefaultAdmin ? 'advanced' : 'basic',
                        saveImageQuality: 'medium'
                    };
                    resolve({
                        outfits: data.outfits || [],
                        lookbooks: data.lookbooks || [],
                        profile: { ...defaultProfile, ...(data.profile || {}) },
                        savedContent: data.savedContent || [],
                        favorites: data.favorites || [],
                    });
                } else {
                    resolve(null);
                }
            } catch (error) {
                reject(new Error('Failed to load data from the simulated cloud.'));
            }
        }, 1500);
    });
};

// --- Social Media Service ---
export const connectInstagram = async (): Promise<{ success: boolean }> => {
    await simulateNetworkDelay(500);
    return { success: true };
};

export const fetchInstagramReels = async (): Promise<InstagramReel[]> => {
    await simulateNetworkDelay(500);
    return instagramReels;
};

export const connectFacebook = async (): Promise<{ success: boolean }> => {
    await simulateNetworkDelay(500);
    return { success: true };
};

export const connectYouTube = async (): Promise<{ success: boolean }> => {
    await simulateNetworkDelay(500);
    return { success: true };
};

export const fetchYouTubePlaylist = async (): Promise<YouTubeVideo[]> => {
    await simulateNetworkDelay(500);
    return youTubeVideos;
};

export const connectTwitter = async (): Promise<{ success: boolean }> => {
    await simulateNetworkDelay(500);
    return { success: true };
};

export const fetchTwitterFeed = async (): Promise<Tweet[]> => {
    await simulateNetworkDelay(500);
    return tweets;
};

// --- AI Services ---
export const mockAnalyzeColor = async (imageUrl: string): Promise<ColorAnalysisResult> => {
    Logger.info('Simulating AI Color Analysis');
    await simulateNetworkDelay(2000);

    const seasons = ['Deep Autumn', 'Light Spring', 'Cool Summer', 'Clear Winter'];
    return {
        season: seasons[Math.floor(Math.random() * seasons.length)],
        characteristics: { skinTone: 'Neutral-Warm', hairColor: 'Dark Brown', eyeColor: 'Hazel', contrast: 'Medium-High', faceShape: 'Oval', bodySize: 'Medium' },
        palette: {
            powerColors: ['#8B0000', '#D2691E', '#556B2F', '#DAA520'],
            neutrals: ['#F5F5DC', '#8B4513', '#2F4F4F', '#000000'],
            avoidColors: ['#FF69B4', '#00FFFF', '#E6E6FA']
        },
        advice: {
            clothing: 'Opt for rich, warm earth tones. Avoid icy pastels that may wash you out.',
            hair: 'Warm highlights or rich chestnut shades will complement your skin tone.',
            makeup: 'Warm terracotta blush and brick-red lipsticks are your best friends.',
            jewelry: 'Gold and copper jewelry will harmonize beautifully with your undertones.'
        }
    };
};
